import { AxiosError } from 'axios'
import { useEffect } from 'react'
import { UseInfiniteQueryResult, useInfiniteQuery, GetNextPageParamFunction } from 'react-query'
import { CityClinic } from '@customTypes/Clinic'
import { usePractitionersContext } from '@context/PractitionersContext'
import { useVerifyPhoneContext } from '@context/VerifyPhoneContext'
import { ClinicAvailabilities } from 'src/services/api/availabilities'
import { useChoiceState } from '@context/ChoiceContext'
import { useConfig } from '@context/ConfigurationContext'
import { useGapFillerQueryParams, useRecallQueryParams } from '../useQueryParams'
import { getQueryParams } from './helpers/getQueryParams'
import { useNearbyClinicIdsWithTreatmentBundles } from './helpers/useNearbyClinicIdsWithTreatmentBundles'

const STALE_TIME = 900000 // 15 minutes in miliseconds

const useAvailabilities = (
  getNextPageParam: GetNextPageParamFunction<ClinicAvailabilities> | undefined,
  isMoreClinicsOpen: boolean,
  clinicsInCity: CityClinic[],
  resetCurrentPeriod: () => void
): UseInfiniteQueryResult<ClinicAvailabilities, AxiosError> => {
  const { clinicId } = useConfig()
  const { treatmentBundleTypeReferenceId, preferredPractitionerReferenceIds, patientType, insuranceType } =
    useChoiceState()
  const { allPractitioners } = usePractitionersContext()
  const { selectedPatient } = useVerifyPhoneContext()
  const { queryParams: recallQueryParams } = useRecallQueryParams()
  const { queryParams: gapFillerQueryParams, hasAllRequiredParams: hasRequiredGapFillerParams } =
    useGapFillerQueryParams()
  const nearbyClinicIds = isMoreClinicsOpen
    ? clinicsInCity.map((clinic) => clinic.referenceId).filter((id) => id !== clinicId)
    : []
  const hasRequiredQueryParams = (!!clinicId && !!treatmentBundleTypeReferenceId) || hasRequiredGapFillerParams
  const nearbyClinics = useNearbyClinicIdsWithTreatmentBundles(isMoreClinicsOpen ? [clinicId, ...nearbyClinicIds] : [])

  const [queryKey, queryFn] = getQueryParams(
    {
      clinicReferenceId: clinicId,
      nearbyClinics: nearbyClinics,
      treatmentBundleTypeReferenceId,
      preferredPractitionerReferenceIds,
      patientType,
      practitioners: allPractitioners,
      patientReferenceId: selectedPatient?.patientReferenceId,
      insuranceType,
    },
    recallQueryParams ?? {},
    gapFillerQueryParams ?? {}
  )

  const queryKeyStringified = JSON.stringify(queryKey)

  const query = useInfiniteQuery(queryKey, queryFn, {
    staleTime: STALE_TIME,
    refetchOnWindowFocus: true,
    getNextPageParam,
    enabled: hasRequiredQueryParams,
  })

  useEffect(() => {
    resetCurrentPeriod()
  }, [queryKeyStringified, resetCurrentPeriod])

  return query as UseInfiniteQueryResult<ClinicAvailabilities, AxiosError>
}

export default useAvailabilities
